import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Scale, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Toaster } from "sonner";

const todayStr = new Date().toISOString().slice(0, 10);

export default function AccountsBalanceSheet() {
    const [data, setData] = useState(null);
    const [asOn, setAsOn] = useState(todayStr);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/accounts/balance-sheet", { params: { as_on: asOn } });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOn]);

    const balanced = data && Math.abs(data.totals.difference) < 1;

    return (
        <div data-testid="accounts-balance-sheet">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Scale className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Balance Sheet</h1>
                    <p className="text-sm text-stone-600 mt-2">Snapshot of what the business owns vs. what it owes, on a given date. Add opening balances, capital and liabilities via the <em>Journal</em>.</p>
                </div>
                <div>
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500">As on</Label>
                    <Input type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} className="rounded-none border-stone-300 mt-1 w-48" data-testid="bs-as-on" />
                </div>
            </div>

            {busy && <div className="bg-white border border-sky-100 p-8 text-center text-stone-500">Calculating…</div>}

            {data && !busy && (
                <>
                    <div className={`mb-4 px-4 py-3 border-2 flex items-center gap-3 ${balanced ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                        {balanced ? <CheckCircle2 className="w-5 h-5 text-emerald-700" /> : <AlertTriangle className="w-5 h-5 text-amber-700" />}
                        <div className="text-sm flex-1">
                            {balanced ? (
                                <>Books are <strong>balanced</strong>: Assets {formatINR(data.totals.assets)} = Liabilities + Equity {formatINR(data.totals.liabilities_and_equity)}.</>
                            ) : (
                                <>The Balance Sheet is <strong>off by {formatINR(Math.abs(data.totals.difference))}</strong>. Usually this means opening capital or liabilities haven't been posted yet — head to the <em>Journal</em> and add them.</>
                            )}
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4">
                        {/* ASSETS */}
                        <div className="bg-white border border-sky-100">
                            <div className="px-4 py-3 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                                <h2 className="font-serif text-xl text-slate-900">Assets</h2>
                                <span className="font-mono font-medium text-sky-700">{formatINR(data.assets.total)}</span>
                            </div>
                            <table className="w-full text-sm">
                                <tbody>
                                    <Line label="Closing inventory (at retail)" amount={data.assets.closing_inventory} />
                                    <Line label="Sundry debtors (unpaid invoices)" amount={data.assets.sundry_debtors} />
                                    <Line label="Cash & bank (collected)" amount={data.assets.cash_and_bank} />
                                    {data.assets.other_assets.length > 0 && (
                                        <Section title="Other assets (opening)">
                                            {data.assets.other_assets.map((r) => <Line key={r.account} label={r.account} amount={r.amount} />)}
                                            <Line label="Other assets total" amount={data.assets.other_assets_total} bold />
                                        </Section>
                                    )}
                                    <tr className="bg-sky-50 border-t border-sky-100">
                                        <td className="px-4 py-3 font-medium text-slate-900">Total assets</td>
                                        <td className="px-4 py-3 text-right font-mono font-medium text-sky-700" data-testid="bs-assets-total">{formatINR(data.assets.total)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* LIABILITIES + EQUITY */}
                        <div className="bg-white border border-sky-100">
                            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
                                <h2 className="font-serif text-xl text-slate-900">Liabilities + Equity</h2>
                                <span className="font-mono font-medium text-amber-700">{formatINR(data.totals.liabilities_and_equity)}</span>
                            </div>
                            <table className="w-full text-sm">
                                <tbody>
                                    <Section title="Liabilities">
                                        {data.liabilities.sundry_creditors.length === 0 ? (
                                            <tr><td colSpan={2} className="px-4 py-2 text-stone-400 italic text-xs">No sundry creditors recorded — add via Journal.</td></tr>
                                        ) : data.liabilities.sundry_creditors.map((r) => (
                                            <Line key={r.account} label={r.account} amount={r.amount} />
                                        ))}
                                        <Line label="GST payable (output GST)" amount={data.liabilities.gst_payable} muted />
                                        <Line label="Liabilities total" amount={data.liabilities.total} bold />
                                    </Section>
                                    <Section title="Equity / Capital">
                                        {data.equity.capital.map((r) => (
                                            <Line key={r.account} label={r.account} amount={r.amount} />
                                        ))}
                                        {data.equity.drawings.map((r) => (
                                            <Line key={r.account} label={`Drawings — ${r.account}`} amount={-r.amount} />
                                        ))}
                                        <Line label="Retained profit (income − expenses to date)" amount={data.equity.retained_profit} muted />
                                        <Line label="Equity total" amount={data.equity.total} bold />
                                    </Section>
                                    <tr className="bg-amber-50 border-t border-amber-100">
                                        <td className="px-4 py-3 font-medium text-slate-900">Total liabilities + equity</td>
                                        <td className="px-4 py-3 text-right font-mono font-medium text-amber-700" data-testid="bs-le-total">{formatINR(data.totals.liabilities_and_equity)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

const Section = ({ title, children }) => (
    <>
        <tr className="bg-stone-50 border-b border-stone-100">
            <td colSpan={2} className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-stone-500">{title}</td>
        </tr>
        {children}
    </>
);
const Line = ({ label, amount, muted = false, bold = false }) => (
    <tr className="border-b border-stone-100">
        <td className={`px-4 py-2 ${bold ? "font-medium" : ""} ${muted ? "text-stone-500 text-xs" : "text-stone-700"}`}>{label}</td>
        <td className={`px-4 py-2 text-right font-mono ${bold ? "font-medium" : ""} ${muted ? "text-stone-500 text-xs" : "text-stone-700"}`}>{formatINR(amount)}</td>
    </tr>
);
